/**
 * SISTEM SAGLIGI — F-TEC / §5.11
 *
 * ================================================================
 * NEDEN AYRI DOSYA
 * ================================================================
 * Bu ekran `Uygulama.tsx` icinde tanimliydi. Klasor sozlesmesi
 * (`docs/02-klasor-sozlesmesi.md`) ekranlarin kendi dosyasinda
 * olmasini istiyor; ayrica giydirme sirasinda ekrani arayan kisi
 * `SistemSagligi.tsx` bulamayip "ekran yok" sanabiliyordu — panel
 * oturumu 04.09.2026'da tam bunu yasadi.
 */
import { useQuery } from "@tanstack/react-query";

import { BosDurum, Buton, Iskelet, Kart } from "@koclukpanel/ui";
import { saglikSemasi, type Saglik } from "@koclukpanel/contracts";

import { AkademikEsikler } from "./AkademikEsikler";
import { DenemeSiralamaAyari } from "./DenemeSiralamaAyari";
import { api } from "./api";

export function SistemSagligi({ ayarDuzenleyebilir = false }: { ayarDuzenleyebilir?: boolean }) {
  const { data, isPending, isError, error, refetch, isFetching } = useQuery<Saglik>({
    queryKey: ["saglik"],
    /**
     * ================================================================
     * HAM `fetch` DEGIL, `api()`
     * ================================================================
     * Burasi ham `fetch` kullaniyordu ve sunucu KAPALIYKEN ekran
     * "Sunucu 500 döndü" diyordu — 06.08.2026'da canli denemede
     * goruldu. Sunucu 500 dondurmemisti; hic yoktu. Aradaki Vite
     * vekili arka uca ulasamayinca kendi 500'unu uretiyor.
     *
     * `api()` bu ayrimi zaten yapiyor (JSON olmayan 5xx =
     * "ulasilamadi") ve o duzeltme 05.08.2026'da yazilmisti. Bu cagri
     * yeri onu ATLIYORDU: duzeltme vardi, buradan gecmiyordu.
     *
     * Ayni sinif hata bu projede tekrarliyor — mekanizma var ama bir
     * yol onu kullanmiyor. `api-tek-kapi.test.ts` artik bunu tutuyor.
     */
    queryFn: () => api<unknown>("/health").then((v) => saglikSemasi.parse(v)),
  });

  if (isPending) return <Iskelet satir={4} />;

  if (isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Sunucuya ulaşılamadı"
        // Ek cumle YOK: `api()` zaten "pnpm dev calisiyor mu" diyor;
        // buraya ayni tavsiye eklenince ekran onu IKI KERE veriyordu.
        // Mesaj tek yerden yonetilirse tekrar imkansiz.
        aciklama={error instanceof Error ? error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <span
          className={
            data.durum === "saglikli"
              ? "bg-basarili-500/10 text-onay-700 rounded-full px-3 py-1 text-sm font-medium"
              : "bg-hata-500/10 text-hata-700 rounded-full px-3 py-1 text-sm font-medium"
          }
        >
          {data.durum === "saglikli" ? "Sistem sağlıklı" : "Sorun var"}
        </span>
        <Buton gorunum="sessiz" onClick={() => void refetch()} yukleniyor={isFetching}>
          Yenile
        </Buton>
      </div>

      {/*
        AKADEMIK ESIK AYNI EKRANDA: izni `sistem.ayar.duzenle` ve bu
        menunun izni de o. Ayri menu acsaydim iki girdinin ikisi de
        ayni iki kisiye gorunurdu; ayri bir ekran degil, ayni ekranin
        ikinci karti.
      */}
      <AkademikEsikler />
      {/* F-EXM-22 — menü erişiminden bağımsız olarak anahtarın kendi izni de doğrulanır. */}
      {ayarDuzenleyebilir && <DenemeSiralamaAyari />}

      <ul className="flex flex-col gap-2">
        {data.bilesenler.map((b) => (
          <Kart key={b.ad} olarak="li" className="flex items-center justify-between px-4 py-3">
            <span className="flex min-w-0 flex-col">
              <span className="text-sm font-medium">{b.ad}</span>
              {/*
                ================================================================
                SEBEP ARTIK GÖRÜNÜR — ÖNCESİNDE YALNIZCA `title` İÇİNDEYDİ
                ================================================================
                Satır "erişilemiyor" diyordu ama **neden** erişilemediği
                yalnızca tooltip'teydi: fareyle üstüne gelen görüyordu,
                klavyeyle gezen görmüyordu, ekran okuyucular `title`ı
                tutarsız okuyor (bazısı hiç okumuyor).

                Burada saklanan şey sıradan bir ayrıntı değil: bir sistem
                arızasının SEBEBİ. "Redis erişilemiyor" ile "Redis
                erişilemiyor: bağlantı reddedildi" arasındaki fark, teknik
                yöneticinin nereye bakacağını bilmesiyle bilmemesi
                arasındaki fark.
              */}
              {b.hata && <span className="text-notr-600 truncate text-xs">{b.hata}</span>}
            </span>
            {/*
              ================================================================
              ÜÇ HÂL — VE ÜÇÜNCÜSÜ KIRMIZI DEĞİL
              ================================================================
              Sunucu `bilinmiyor` da dönüyor (worker'ın Redis okuması
              düşerse) ve onu SAĞLIKSIZ SAYMIYOR: bütünlük hesabı
              `!== "erisilemiyor"` bakıyor.

              Ekran da öyle davranıyor. Ölçemediğimiz bir bileşeni arıza
              göstermek izleme aracını sürekli kırmızı tutar ve bir süre
              sonra kimse bakmaz — kırmızının değeri, nadir olmasından
              geliyor.

              Metin de "bozuk" değil "ölçülemedi": ikisi ayrı şey ve
              teknik yöneticiye ayrı iş emri veriyor. "Erişilemiyor" =
              git düzelt; "ölçülemedi" = önce ölçümü kur.

              Bu ekran bir tur boyunca bu dalı çizemedi çünkü paylaşılan
              şema üçüncü hâli tanımıyordu ve `parse` atıyordu — yani
              worker ölçülemediği an, ne bozulduğunu söylemesi gereken
              ekranın kendisi bozuluyordu. Şema düzeldi (`008574d`).
            */}
            <span
              className={
                b.durum === "calisiyor"
                  ? "text-onay-700 shrink-0 text-sm"
                  : b.durum === "bilinmiyor"
                    ? "text-notr-600 shrink-0 text-sm"
                    : "text-hata-700 shrink-0 text-sm font-medium"
              }
            >
              {b.durum === "calisiyor"
                ? "çalışıyor"
                : b.durum === "bilinmiyor"
                  ? "ölçülemedi"
                  : "erişilemiyor"}
            </span>
          </Kart>
        ))}
      </ul>
    </div>
  );
}
